/**
 * MotherGeneralTributes — the tributes wall, read on every assembly page.
 *
 * These were not written for this screen. Two hundred and fifty-odd of them
 * were left on the memorial site from 2016 onwards and are read here in the
 * order they were written, oldest first, the way a book of condolence reads.
 * Nothing is reset and nothing is seeded: if the wall is empty, the section
 * does not appear at all rather than showing invented words.
 */
import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Flame, Loader2, Quote } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const PAGE = 12;

/** One date format on the wall: the imported rows read "13 Apr 2016" and a
 *  tribute posted today should read the same way, not "2026-06-14". */
const readableDate = (t) => {
  if (!t.posted_ts) return t.date || '';
  return new Date(t.posted_ts * 1000).toLocaleDateString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric',
  });
};

const fetchPage = async (offset, limit) => {
  const r = await fetch(`${API}/api/tributes/mother-general?offset=${offset}&limit=${limit}`);
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return r.json();
};

const TributeCard = ({ t }) => (
  <figure
    className="break-inside-avoid mb-5 rounded-sm border border-[#e3dcc9] bg-[#fbf8f1] px-6 py-5 transition-colors duration-300 hover:border-[#c9992b]"
    data-testid={`mg-tribute-${t.id}`}
  >
    {t.image_url ? (
      <img
        src={t.image_url}
        alt={t.name}
        loading="lazy"
        className="mb-4 h-40 w-full rounded-sm object-cover"
        data-testid={`mg-tribute-image-${t.id}`}
        onError={(e) => { e.currentTarget.style.display = 'none'; }}
      />
    ) : null}
    {t.title ? (
      <h3 className="font-serif text-lg text-[#1c2a63]" data-testid={`mg-tribute-title-${t.id}`}>
        {t.title}
      </h3>
    ) : null}
    <Quote className="mt-1 h-4 w-4 text-[#c9992b]" aria-hidden="true" />
    <blockquote
      className="mt-2 whitespace-pre-line text-[15px] leading-relaxed text-[#2a2a28]"
      data-testid={`mg-tribute-message-${t.id}`}
    >
      {t.message}
    </blockquote>
    <figcaption className="mt-4 border-t border-[#e3dcc9] pt-3 text-xs tracking-wide text-[#6b6557]">
      <span className="font-medium text-[#1c2a63]" data-testid={`mg-tribute-name-${t.id}`}>
        {t.name}
      </span>
      {t.assembly ? (
        <span data-testid={`mg-tribute-assembly-${t.id}`}> · {t.assembly}</span>
      ) : null}
      {t.date ? <span className="text-[#8d8676]"> · {readableDate(t)}</span> : null}
    </figcaption>
  </figure>
);

export const MotherGeneralTributes = () => {
  const [limit, setLimit] = useState(PAGE);
  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['mg-tributes', limit],
    queryFn: () => fetchPage(0, limit),
    keepPreviousData: true,
  });

  if (isLoading) {
    return (
      <section className="py-16 text-center" data-testid="mg-tributes-loading">
        <Loader2 className="mx-auto h-5 w-5 animate-spin text-[#c9992b]" />
      </section>
    );
  }

  const items = data?.items || [];
  const total = data?.total || 0;
  if (!total) return null;

  return (
    <section
      id="tributes"
      className="border-y border-[#e3dcc9] bg-[#f6f1e6] py-16"
      data-testid="mother-general-tributes"
    >
      <div className="mx-auto max-w-6xl px-4">
        <header className="max-w-2xl">
          <p className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-[#c9992b]">
            <Flame className="h-3.5 w-3.5" aria-hidden="true" /> In loving memory
          </p>
          <h2
            className="mt-3 font-serif text-3xl text-[#1c2a63] md:text-4xl"
            data-testid="mg-tributes-heading"
          >
            {data?.heading || 'Tributes to Mother General'}
          </h2>
          <p className="mt-3 text-sm text-[#6b6557]" data-testid="mg-tributes-count">
            {data?.subhead ? `${data.subhead} · ` : ''}{total} {total === 1 ? 'tribute' : 'tributes'}, the earliest first.
          </p>
        </header>

        <div className="mt-10 columns-1 gap-5 sm:columns-2 lg:columns-3" data-testid="mg-tributes-list">
          {items.map((t) => <TributeCard key={t.id} t={t} />)}
        </div>

        {items.length < total && (
          <div className="mt-6 text-center">
            <button
              type="button"
              onClick={() => setLimit((l) => l + PAGE)}
              disabled={isFetching}
              className="rounded-full border border-[#1c2a63] px-6 py-2 text-sm text-[#1c2a63] transition-colors duration-200 hover:bg-[#1c2a63] hover:text-white disabled:opacity-60"
              data-testid="mg-tributes-load-more"
            >
              {isFetching
                ? <Loader2 className="h-4 w-4 animate-spin" />
                : `Read more tributes (${total - items.length} remaining)`}
            </button>
          </div>
        )}
      </div>
    </section>
  );
};

export default MotherGeneralTributes;
